
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { getImagePreviewPath } from "./utils/imageHandlers";

interface StoryEditorMetadataProps {
  formState: any;
  onChange: (newState: any) => void;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

const StoryEditorMetadata: React.FC<StoryEditorMetadataProps> = ({ 
  formState, 
  onChange, 
  onImageUpload,
  isUploading,
  fileInputRef
}) => {
  const { language } = useLanguage();
  
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  // Handle image load errors with better logging
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error("Preview image failed to load:", e.currentTarget.src);
    e.currentTarget.src = "/placeholder.svg";
    e.currentTarget.classList.add("bg-gray-100");
  };
  
  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground">
        {language === "en" ? "Story Metadata" : "بيانات وصفية للقصة"}
      </h3>
      
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="date">
          {language === "en" ? "Date" : "التاريخ"}
        </label>
        <Input 
          id="date"
          type="date"
          value={formState.date.substring(0, 10)} 
          onChange={(e) => onChange({ ...formState, date: e.target.value })}
        />
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="slug">
          {language === "en" ? "URL Slug" : "الرابط المختصر"}
        </label>
        <Input 
          id="slug"
          value={formState.slug} 
          onChange={(e) => onChange({ ...formState, slug: e.target.value })}
        />
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="image">
          {language === "en" ? "Image" : "الصورة"}
        </label>
        
        <div className="flex gap-2">
          <Input 
            id="image"
            value={formState.image} 
            onChange={(e) => onChange({ ...formState, image: e.target.value })}
            placeholder={language === "en" ? "Image path or URL" : "مسار الصورة أو الرابط"}
            className="flex-1"
          />
          
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={onImageUpload}
          />
          
          <Button 
            type="button" 
            variant="outline" 
            onClick={triggerFileInput}
            disabled={isUploading}
            className="flex-shrink-0"
          >
            {isUploading ? (
              <div className="flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {language === "en" ? "Uploading..." : "جاري التحميل..."}
              </div>
            ) : (
              <div className="flex items-center">
                <Upload className="mr-2 h-4 w-4" />
                {language === "en" ? "Upload" : "تحميل"}
              </div>
            )}
          </Button>
        </div>
        
        {formState.image && (
          <div className="mt-2 relative w-full h-32 rounded-md overflow-hidden border border-muted">
            <img 
              src={getImagePreviewPath(formState.image)}
              alt="Story preview" 
              className="w-full h-full object-cover"
              onError={handleImageError}
              data-story-id={formState.id}
            />
          </div>
        )}
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="featured" 
          checked={formState.featured}
          onCheckedChange={(checked) => 
            onChange({ ...formState, featured: checked === true })
          }
        />
        <label
          htmlFor="featured"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {language === "en" ? "Feature on home page" : "عرض على الصفحة الرئيسية"}
        </label>
      </div>
    </div>
  );
};

export default StoryEditorMetadata;
